import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Globe, CloudUpload, CheckCircle, AlertTriangle, Clock, RefreshCw,
  Download, Send, Loader2, FileText, Shield, Eye, Code,
  ArrowDownToLine, History, BarChart3, Inbox, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ANAF Status Config
const ANAF_STATUS_CONFIG = {
  netrimisa: { bg: "bg-slate-100 text-slate-600", label: "Netrimisă", icon: FileText },
  trimisa: { bg: "bg-amber-100 text-amber-700", label: "Trimisă", icon: CloudUpload },
  in_procesare: { bg: "bg-blue-100 text-blue-700", label: "În procesare", icon: Clock },
  acceptata: { bg: "bg-emerald-100 text-emerald-700", label: "Acceptată", icon: CheckCircle },
  respinsa: { bg: "bg-red-100 text-red-700", label: "Respinsă", icon: AlertTriangle },
  eroare: { bg: "bg-red-100 text-red-700", label: "Eroare", icon: AlertTriangle },
};

const RECEIVED_STATUS_CONFIG = {
  noua: { bg: "bg-blue-100 text-blue-700", label: "Nouă" },
  vizualizata: { bg: "bg-slate-100 text-slate-600", label: "Vizualizată" },
  importata: { bg: "bg-green-100 text-green-700", label: "Importată" },
  respinsa_manual: { bg: "bg-red-100 text-red-700", label: "Respinsă" },
};

export default function AnafDashboard({ companyId }) {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [invoicesByStatus, setInvoicesByStatus] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("netrimisa");
  const [activityLog, setActivityLog] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [receivedInvoices, setReceivedInvoices] = useState([]);
  const [selectedReceived, setSelectedReceived] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/anaf/dashboard-stats?company_id=${companyId}`);
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, [companyId]);

  const fetchInvoicesByStatus = useCallback(async (status) => {
    try {
      const res = await axios.get(`${API}/anaf/invoices-by-status?company_id=${companyId}&anaf_status=${status}`);
      setInvoicesByStatus(res.data);
    } catch (e) { console.error(e); }
  }, [companyId]);

  const fetchActivityLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/anaf/activity-log?company_id=${companyId}&limit=50`);
      setActivityLog(res.data.logs);
      setActivityTotal(res.data.total);
    } catch (e) { console.error(e); }
  }, [companyId]);

  const fetchReceived = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/anaf/received?company_id=${companyId}`);
      setReceivedInvoices(res.data);
    } catch (e) { console.error(e); }
  }, [companyId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "invoices") fetchInvoicesByStatus(selectedStatusFilter);
    if (activeTab === "activity") fetchActivityLog();
    if (activeTab === "received") fetchReceived();
  }, [activeTab, selectedStatusFilter, fetchInvoicesByStatus, fetchActivityLog, fetchReceived]);

  const handleBulkSend = async () => {
    if (selectedInvoiceIds.length === 0) {
      toast.info("Selectați facturile de trimis");
      return;
    }
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/anaf/bulk-send?company_id=${companyId}`, { invoice_ids: selectedInvoiceIds });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => toast.error(err));
      }
      setSelectedInvoiceIds([]);
      fetchInvoicesByStatus(selectedStatusFilter);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la trimitere");
    } finally { setBulkSending(false); }
  };

  const handleBulkCheckStatus = async () => {
    setBulkChecking(true);
    try {
      const res = await axios.post(`${API}/anaf/bulk-check-status?company_id=${companyId}`);
      toast.success(res.data.message);
      fetchInvoicesByStatus(selectedStatusFilter);
      fetchStats();
    } catch (e) {
      toast.error("Eroare la verificare status");
    } finally { setBulkChecking(false); }
  };

  const handleSendSingle = async (invoiceId) => {
    setSendingId(invoiceId);
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/anaf/send?company_id=${companyId}`);
      if (res.data.success) toast.success(res.data.message || "Trimis!");
      else toast.error(res.data.message || "Eroare");
      fetchInvoicesByStatus(selectedStatusFilter);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare");
    } finally { setSendingId(null); }
  };

  const handleSyncReceived = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/anaf/sync-received?company_id=${companyId}`);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message || "Eroare sincronizare");
      fetchReceived();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare sincronizare");
    } finally { setSyncing(false); }
  };

  const handleReceivedAction = async (receivedId, action) => {
    try {
      const res = await axios.put(`${API}/anaf/received/${receivedId}?company_id=${companyId}`, { action });
      toast.success(res.data.message);
      fetchReceived();
      fetchStats();
      setSelectedReceived(null);
    } catch (e) {
      toast.error("Eroare la actualizare");
    }
  };

  const handleViewReceived = async (receivedId) => {
    try {
      const res = await axios.get(`${API}/anaf/received/${receivedId}?company_id=${companyId}`);
      setSelectedReceived(res.data);
    } catch (e) {
      toast.error("Eroare la încărcare");
    }
  };

  const handleFullSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/anaf/trigger-sync?company_id=${companyId}`);
      toast.success(res.data.message);
      fetchStats();
      if (activeTab === "invoices") fetchInvoicesByStatus(selectedStatusFilter);
      if (activeTab === "received") fetchReceived();
      if (activeTab === "activity") fetchActivityLog();
    } catch (e) {
      toast.error("Eroare sincronizare");
    } finally { setSyncing(false); }
  };

  const toggleInvoiceSelection = (id) => {
    setSelectedInvoiceIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllInvoices = () => {
    if (selectedInvoiceIds.length === invoicesByStatus.length) {
      setSelectedInvoiceIds([]);
    } else {
      setSelectedInvoiceIds(invoicesByStatus.map(i => i.id));
    }
  };

  const formatNumber = (n) => (n || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = (d) => {
    if (!d) return "-";
    try { return new Date(d).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  if (!stats) return (
    <div className="flex justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="anaf-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-teal-600" />
          <div>
            <h2 className="text-lg font-bold">ANAF e-Factura</h2>
            <p className="text-sm text-slate-500">
              {stats.is_configured ? "Sistem conectat" : "Neconfigurat - adăugați credențialele ANAF"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleFullSync}
            disabled={syncing}
            data-testid="anaf-full-sync-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sincronizare Completă
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: "Netrimise", count: stats.netrimise, color: "bg-slate-50 border-slate-200 text-slate-700", filter: "netrimisa" },
          { label: "Trimise", count: stats.trimise, color: "bg-amber-50 border-amber-200 text-amber-700", filter: "trimisa" },
          { label: "Procesare", count: stats.in_procesare, color: "bg-blue-50 border-blue-200 text-blue-700", filter: "in_procesare" },
          { label: "Acceptate", count: stats.acceptate, color: "bg-emerald-50 border-emerald-200 text-emerald-700", filter: "acceptata" },
          { label: "Respinse", count: stats.respinse, color: "bg-red-50 border-red-200 text-red-700", filter: "respinsa" },
          { label: "Erori", count: stats.erori, color: "bg-red-50 border-red-200 text-red-700", filter: "eroare" },
          { label: "Primite", count: stats.received_new > 0 ? `${stats.received_total} (${stats.received_new} noi)` : stats.received_total, color: "bg-teal-50 border-teal-200 text-teal-700", filter: null },
        ].map((s) => (
          <Card
            key={s.label}
            className={`${s.color} border cursor-pointer hover:shadow-md transition-shadow`}
            onClick={() => {
              if (s.filter) { setSelectedStatusFilter(s.filter); setActiveTab("invoices"); }
              else { setActiveTab("received"); }
            }}
            data-testid={`anaf-stat-${s.filter || 'received'}`}
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{s.count}</p>
              <p className="text-xs font-medium">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Auto-send indicator */}
      {stats.auto_send_enabled && (
        <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg">
          <Clock className="w-4 h-4 text-teal-600" />
          <span className="text-sm text-teal-800 font-medium">
            Trimitere automată activă - facturile se trimit automat conform setărilor
          </span>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="overview" data-testid="anaf-tab-overview">
            <BarChart3 className="w-4 h-4 mr-1" /> Sumar
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="anaf-tab-invoices">
            <FileText className="w-4 h-4 mr-1" /> Facturi
          </TabsTrigger>
          <TabsTrigger value="received" data-testid="anaf-tab-received">
            <Inbox className="w-4 h-4 mr-1" /> Primite
            {stats.received_new > 0 && (
              <Badge className="ml-1 bg-teal-600 text-xs">{stats.received_new}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="anaf-tab-activity">
            <History className="w-4 h-4 mr-1" /> Jurnal
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Activitate Recentă</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recent_activity.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Nu există activitate recentă</p>
              ) : (
                <div className="space-y-2">
                  {stats.recent_activity.map((log) => (
                    <div key={log.id || log.created_at} className="flex items-center gap-3 p-2 rounded bg-slate-50">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{log.details || log.action}</p>
                        <p className="text-xs text-slate-500">{log.invoice_series && `${log.invoice_series} | `}{formatDate(log.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVOICES TAB */}
        <TabsContent value="invoices" className="space-y-4">
          {/* Status filter pills */}
          <div className="flex gap-2 flex-wrap">
            {Object.entries(ANAF_STATUS_CONFIG).map(([key, conf]) => (
              <Button
                key={key}
                variant={selectedStatusFilter === key ? "default" : "outline"}
                size="sm"
                onClick={() => { setSelectedStatusFilter(key); setSelectedInvoiceIds([]); }}
                className={selectedStatusFilter === key ? "bg-teal-600 hover:bg-teal-700" : ""}
                data-testid={`anaf-filter-${key}`}
              >
                {conf.label}
              </Button>
            ))}
          </div>

          {/* Bulk actions */}
          <div className="flex gap-2 flex-wrap">
            {selectedStatusFilter === "netrimisa" && (
              <Button
                size="sm"
                onClick={handleBulkSend}
                disabled={bulkSending || selectedInvoiceIds.length === 0}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="anaf-bulk-send-btn"
              >
                {bulkSending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Trimite selectate ({selectedInvoiceIds.length})
              </Button>
            )}
            {(selectedStatusFilter === "trimisa" || selectedStatusFilter === "in_procesare") && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkCheckStatus}
                disabled={bulkChecking}
                data-testid="anaf-bulk-check-btn"
              >
                {bulkChecking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Verifică toate statusurile
              </Button>
            )}
          </div>

          {/* Invoice list */}
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectedStatusFilter === "netrimisa" && (
                        <TableHead className="w-10">
                          <input
                            type="checkbox"
                            checked={selectedInvoiceIds.length === invoicesByStatus.length && invoicesByStatus.length > 0}
                            onChange={selectAllInvoices}
                            className="w-4 h-4"
                          />
                        </TableHead>
                      )}
                      <TableHead>Serie/Nr.</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status ANAF</TableHead>
                      <TableHead className="text-right">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesByStatus.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          Nu există facturi cu acest status
                        </TableCell>
                      </TableRow>
                    ) : invoicesByStatus.map(inv => {
                      const sc = ANAF_STATUS_CONFIG[inv.anaf_status || "netrimisa"] || ANAF_STATUS_CONFIG.netrimisa;
                      const StatusIcon = sc.icon;
                      return (
                        <TableRow key={inv.id}>
                          {selectedStatusFilter === "netrimisa" && (
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedInvoiceIds.includes(inv.id)}
                                onChange={() => toggleInvoiceSelection(inv.id)}
                                className="w-4 h-4"
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">{inv.series}/{inv.number}</TableCell>
                          <TableCell>{inv.invoice_date}</TableCell>
                          <TableCell>
                            <p className="font-medium truncate max-w-[200px]">{inv.client_name}</p>
                            {inv.client_cui && <p className="text-xs text-slate-500">CUI: {inv.client_cui}</p>}
                          </TableCell>
                          <TableCell className="text-right font-semibold">{formatNumber(inv.total)} RON</TableCell>
                          <TableCell>
                            <Badge className={`${sc.bg} text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />{sc.label}
                            </Badge>
                            {inv.anaf_auto_sent && <Badge className="ml-1 bg-teal-100 text-teal-700 text-xs">Auto</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {!inv.anaf_status && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleSendSingle(inv.id)}
                                  disabled={sendingId === inv.id}
                                  title="Trimite la ANAF"
                                  className="text-teal-600"
                                >
                                  {sendingId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                              )}
                              {inv.anaf_status === "acceptata" && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/invoices/${inv.id}/anaf/download-signed?company_id=${companyId}`, { responseType: 'blob' });
                                      const url = window.URL.createObjectURL(new Blob([res.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', `semnat_${inv.series}${inv.number}.xml`);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      toast.success("Factură semnată descărcată!");
                                    } catch (e) {
                                      toast.error(e.response?.data?.detail || "Eroare descărcare");
                                    }
                                  }}
                                  title="Descarcă factură semnată"
                                  className="text-emerald-600"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              )}
                              {inv.anaf_status === "eroare" && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleSendSingle(inv.id)}
                                  disabled={sendingId === inv.id}
                                  title="Retrimite"
                                  className="text-amber-600"
                                >
                                  {sendingId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                </Button>
                              )}
                              {inv.anaf_error && (
                                <span className="text-xs text-red-500 max-w-[150px] truncate" title={inv.anaf_error}>
                                  {inv.anaf_error}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RECEIVED TAB */}
        <TabsContent value="received" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold flex items-center gap-2">
              <Inbox className="w-5 h-5 text-teal-600" />
              Facturi Primite de la ANAF
            </h3>
            <Button
              size="sm"
              onClick={handleSyncReceived}
              disabled={syncing}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="anaf-sync-received-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowDownToLine className="w-4 h-4 mr-1" />}
              Sincronizează de la ANAF
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nr. Factură</TableHead>
                      <TableHead>Furnizor</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receivedInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          Nu există facturi primite. Apăsați "Sincronizează" pentru a descărca de la ANAF.
                        </TableCell>
                      </TableRow>
                    ) : receivedInvoices.map(inv => {
                      const rc = RECEIVED_STATUS_CONFIG[inv.status] || RECEIVED_STATUS_CONFIG.noua;
                      return (
                        <TableRow key={inv.id} className={inv.status === "noua" ? "bg-blue-50/50" : ""}>
                          <TableCell className="font-medium">{inv.invoice_number_xml || "-"}</TableCell>
                          <TableCell>
                            <p className="font-medium truncate max-w-[200px]">{inv.supplier_name || "Necunoscut"}</p>
                            {inv.supplier_cui && <p className="text-xs text-slate-500">CUI: {inv.supplier_cui}</p>}
                          </TableCell>
                          <TableCell>{inv.issue_date || "-"}</TableCell>
                          <TableCell className="text-right font-semibold">{formatNumber(inv.total)} {inv.currency || "RON"}</TableCell>
                          <TableCell>
                            <Badge className={rc.bg}>{rc.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => handleViewReceived(inv.id)} title="Vizualizare" data-testid={`view-received-${inv.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {(inv.status === "noua" || inv.status === "vizualizata") && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handleReceivedAction(inv.id, "importata")} className="text-green-600" title="Importă">
                                    <ArrowDownToLine className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleReceivedAction(inv.id, "respinsa_manual")} className="text-red-500" title="Respinge">
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ACTIVITY LOG TAB */}
        <TabsContent value="activity" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold flex items-center gap-2">
              <History className="w-5 h-5 text-teal-600" />
              Jurnal Activitate ANAF
              <Badge variant="outline">{activityTotal} înregistrări</Badge>
            </h3>
            <Button variant="outline" size="sm" onClick={fetchActivityLog}>
              <RefreshCw className="w-4 h-4 mr-1" /> Reîncarcă
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">St.</TableHead>
                      <TableHead>Acțiune</TableHead>
                      <TableHead>Factură</TableHead>
                      <TableHead>Detalii</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityLog.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          Nu există activitate înregistrată
                        </TableCell>
                      </TableRow>
                    ) : activityLog.map(log => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <div className={`w-3 h-3 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {log.action.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{log.invoice_series || "-"}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[300px] truncate">{log.details || "-"}</TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Received Invoice Detail Dialog */}
      <Dialog open={!!selectedReceived} onOpenChange={() => setSelectedReceived(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-teal-600" />
              Factură Primită - {selectedReceived?.invoice_number_xml}
            </DialogTitle>
            <DialogDescription>Detalii factură primită de la ANAF</DialogDescription>
          </DialogHeader>

          {selectedReceived && (
            <div className="space-y-4">
              {/* Supplier info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Furnizor</h4>
                  <p className="font-medium">{selectedReceived.supplier_name}</p>
                  {selectedReceived.supplier_cui && <p className="text-sm text-slate-500">CUI: {selectedReceived.supplier_cui}</p>}
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Detalii</h4>
                  <p className="text-sm">Data: {selectedReceived.issue_date}</p>
                  {selectedReceived.due_date && <p className="text-sm">Scadent: {selectedReceived.due_date}</p>}
                  <p className="text-sm font-bold mt-1">Total: {formatNumber(selectedReceived.total)} {selectedReceived.currency}</p>
                </div>
              </div>

              {/* Items */}
              {selectedReceived.items && selectedReceived.items.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Produse/Servicii</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Denumire</TableHead>
                        <TableHead>UM</TableHead>
                        <TableHead className="text-right">Cant.</TableHead>
                        <TableHead className="text-right">Preț</TableHead>
                        <TableHead className="text-right">TVA%</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReceived.items.map((item, i) => (
                        <TableRow key={`${item.name}-${item.quantity}-${i}`}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.um}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{formatNumber(item.unit_price)}</TableCell>
                          <TableCell className="text-right">{item.vat_percent}%</TableCell>
                          <TableCell className="text-right font-medium">{formatNumber(item.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* XML Preview */}
              {selectedReceived.xml_content && (
                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Code className="w-4 h-4" /> XML Original
                  </h4>
                  <pre className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs overflow-auto max-h-[200px] font-mono">
                    {selectedReceived.xml_content}
                  </pre>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedReceived(null)}>Închide</Button>
                {(selectedReceived.status === "noua" || selectedReceived.status === "vizualizata") && (
                  <>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-300"
                      onClick={() => handleReceivedAction(selectedReceived.id, "respinsa_manual")}
                    >
                      <X className="w-4 h-4 mr-1" /> Respinge
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleReceivedAction(selectedReceived.id, "importata")}
                    >
                      <ArrowDownToLine className="w-4 h-4 mr-1" /> Importă în Facturi
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
